import React, { useState, useEffect } from "react";
import { Tooltip } from "flowbite-react";
import { ArrowUp, ArrowDown, Info, HandCoins } from "@phosphor-icons/react";
import NumberFlow, { type Format } from "@number-flow/react";
import { CurrencyCode } from "../shared/constants";
import { formatCurrency } from "../lib/utils";
import { useTranslation } from "react-i18next";

// ─── Types ────────────────────────────────────────────────────────────────────

type AccentColor = "indigo" | "green" | "amber" | "blue" | "purple" | "gray";

interface BreakdownItem {
  label: string;
  value: number;
  withTaxValue?: number;
  icon?: string;
  valueClassName?: string;
  tooltipItems?: {
    label: string;
    value: number;
    labelClassName?: string;
    valueClassName?: string;
    children?: {
      label: string;
      value: number;
      labelClassName?: string;
      valueClassName?: string;
    }[];
  }[];
}

interface StatusPill {
  label: string;
  value: number;
  color: "green" | "amber" | "red" | "blue" | "gray";
}

interface StatCardProps {
  title: string;
  value: string | number;
  isLoading?: boolean;
  previousValue?: number;
  isCurrency?: boolean;
  isPercentage?: boolean;
  icon?: React.ReactNode;
  accentColor?: AccentColor;
  onClick?: () => void;
  currency?: CurrencyCode;
  withTaxValue?: number;
  showWithTax?: boolean;
  statusPills?: StatusPill[];
  breakdown?: BreakdownItem[];
  tooltip?: boolean;
  tooltipContent?: React.ReactNode;
  tooltipDirection?: "top" | "bottom" | "left" | "right";
  // Fraction digits for the main value (default: 0 for INR, 2 otherwise)
  decimals?: number;
  // ── Legacy props ──────────────────────────────────────────────────────────
  isInAppPurchase?: boolean;
  totalEstimatedRevenue?: {
    value: number;
    withTaxValue?: number;
    label: string;
    labelClassName?: string;
    valueClassName?: string;
    tooltipItems?: {
      label: string;
      value: number;
      labelClassName?: string;
      valueClassName?: string;
      children?: { label: string; value: number }[];
    }[];
  }[];
  subStats?: { value: number | string; label: string }[];
  color?: string;
}

// ─── Accent colour maps ───────────────────────────────────────────────────────

const accentBg: Record<AccentColor, string> = {
  indigo: "bg-indigo-50 dark:bg-indigo-500/15 text-indigo-600 dark:text-indigo-400",
  green: "bg-green-50 dark:bg-green-500/15 text-green-600 dark:text-green-400",
  amber: "bg-amber-50 dark:bg-amber-500/15 text-amber-600 dark:text-amber-400",
  blue: "bg-blue-50 dark:bg-blue-500/15 text-blue-600 dark:text-blue-400",
  purple: "bg-purple-50 dark:bg-purple-500/15 text-purple-600 dark:text-purple-400",
  gray: "bg-gray-100 dark:bg-neutral-700 text-gray-500 dark:text-gray-400",
};

const pillColors: Record<StatusPill["color"], string> = {
  green: "bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-400",
  amber: "bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400",
  red: "bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400",
  blue: "bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400",
  gray: "bg-gray-100 dark:bg-neutral-700 text-gray-600 dark:text-gray-400",
};

// ─── Animated number helper ───────────────────────────────────────────────────

interface AnimatedValueProps {
  value: number;
  isCurrency?: boolean;
  isPercentage?: boolean;
  currency?: CurrencyCode;
  className?: string;
  decimals?: number;
}

const AnimatedValue: React.FC<AnimatedValueProps> = ({
  value,
  isCurrency,
  isPercentage,
  currency = "USD",
  className,
  decimals,
}) => {
  // Start at 0 so NumberFlow animates from 0 → real value on initial mount
  const [displayVal, setDisplayVal] = useState(0);
  useEffect(() => {
    setDisplayVal(value);
  }, [value]);

  if (isCurrency) {
    const fmt: Format = {
      style: "currency",
      currency,
      minimumFractionDigits: decimals ?? (currency === "INR" ? 0 : 2),
      maximumFractionDigits: decimals ?? (currency === "INR" ? 0 : 2),
    };
    return <NumberFlow value={displayVal} format={fmt} locales="en-US" className={className} />;
  }
  if (isPercentage) {
    return (
      <NumberFlow
        value={displayVal}
        suffix="%"
        format={{ maximumFractionDigits: decimals ?? 2, minimumFractionDigits: decimals ?? 2 }}
        locales="en-US"
        className={className}
      />
    );
  }
  return (
    <NumberFlow
      value={displayVal}
      format={{ useGrouping: true, maximumFractionDigits: decimals ?? 0 }}
      locales="en-US"
      className={className}
    />
  );
};

// ─── Component ────────────────────────────────────────────────────────────────

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  isLoading = false,
  previousValue,
  isCurrency = false,
  isPercentage = false,
  icon,
  accentColor = "indigo",
  onClick,
  currency,
  withTaxValue,
  showWithTax,
  statusPills,
  breakdown,
  tooltip = false,
  tooltipContent,
  tooltipDirection = "bottom",
  isInAppPurchase,
  totalEstimatedRevenue,
  subStats,
  decimals,
}) => {
  const { t } = useTranslation();

  const percentChange =
    previousValue && typeof value === "number"
      ? ((value - previousValue) / previousValue) * 100
      : 0;
  const isPositive = percentChange >= 0;

  const withTaxEnabled = showWithTax && withTaxValue !== undefined;

  // Static formatter for non-animated contexts (tooltips, etc.)
  const fmtStatic = (val: number | string) => {
    if (typeof val === "string") return val;
    if (isCurrency) return formatCurrency(val, currency);
    if (isPercentage) return `${val.toFixed(2)}%`;
    return val.toLocaleString();
  };

  // Normalise legacy totalEstimatedRevenue → breakdown
  const resolvedBreakdown: BreakdownItem[] | undefined =
    breakdown ??
    (isInAppPurchase && totalEstimatedRevenue?.length
      ? totalEstimatedRevenue.map((item) => ({
        label: item.label,
        value: item.value,
        withTaxValue: item.withTaxValue,
        valueClassName: item.valueClassName,
        tooltipItems: item.tooltipItems,
      }))
      : undefined);

  // Main display value
  const displayValue = withTaxEnabled ? withTaxValue : value;

  return (
    <div
      className={`flex flex-col rounded-xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 shadow-sm transition-all duration-200 overflow-hidden ${onClick ? "cursor-pointer hover:shadow-md hover:scale-[1.01]" : ""
        }`}
      onClick={onClick}
    >
      {/* ── Zone 1: Header ── */}
      <div className="flex items-start justify-between px-4 pt-4 pb-2">
        <div className="flex-1 min-w-0">
          {/* Title row */}
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest flex items-center gap-1.5">
            {title}
            {withTaxEnabled && (
              <Tooltip content={t("common.valuesWithTax")} placement="top">
                <HandCoins
                  size={14}
                  weight="duotone"
                  className="text-indigo-400 cursor-help flex-shrink-0"
                />
              </Tooltip>
            )}
            {tooltip && (
              <Tooltip content={tooltipContent} placement={tooltipDirection}>
                <Info size={13} className="text-gray-300 hover:text-gray-500 cursor-help flex-shrink-0" />
              </Tooltip>
            )}
          </p>

          <div className="flex items-center gap-4 mt-1.5" >
            {/* ── Zone 2: Main value ── */}
            <div >
              {isLoading ? (
                <div className="h-8 w-24 rounded-md bg-gray-200 dark:bg-neutral-700 animate-pulse" />
              ) : typeof displayValue === "number" ? (
                <AnimatedValue
                  value={displayValue}
                  isCurrency={isCurrency}
                  isPercentage={isPercentage}
                  currency={currency}
                  decimals={decimals}
                  className="text-2xl font-bold text-gray-800 dark:text-gray-100 leading-none"
                />
              ) : (
                <span className="text-2xl font-bold text-gray-800 dark:text-gray-100 leading-none">
                  {displayValue}
                </span>
              )}
            </div>

            {/* Status pills */}
            {statusPills && statusPills.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5">
                {statusPills.map((pill) => (
                  <span
                    key={pill.label}
                    className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${pillColors[pill.color]}`}
                  >
                    <NumberFlow value={pill.value} format={{ useGrouping: true, maximumFractionDigits: 0 }} locales="en-US" />
                    <span className="font-normal opacity-80">{pill.label}</span>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Percent change */}
          {previousValue !== undefined && (
            <div className="flex items-center mt-2">
              <span className={`text-xs font-semibold ${isPositive ? "text-green-600" : "text-red-500"}`}>
                {isPositive ? <ArrowUp size={12} className="inline" /> : <ArrowDown size={12} className="inline" />}
                {Math.abs(percentChange).toFixed(1)}%
              </span>
              <span className="text-[10px] text-gray-400 ml-1">{t("common.vsPreviousPeriod")}</span>
            </div>
          )}
        </div>

        {/* Icon badge */}
        {icon && (
          <div className={`flex-shrink-0 ml-3 p-2.5 rounded-xl ${accentBg[accentColor]}`}>
            {icon}
          </div>
        )}
      </div>

      {/* ── Zone 3: Breakdown section ── */}
      {resolvedBreakdown && resolvedBreakdown.length > 0 && (
        <div className="border-t border-gray-100 dark:border-neutral-700 px-4 py-3 bg-gray-50/50 dark:bg-neutral-800/50">
          <div className="flex items-start gap-1 flex-wrap">
            {resolvedBreakdown.map((item, idx) => {
              const breakdownDisplayVal = (showWithTax && item.withTaxValue !== undefined)
                ? item.withTaxValue
                : item.value;
              const isZero = breakdownDisplayVal === 0;

              const chip = (
                <div
                  key={item.label}
                  className={`flex-1 min-w-0 flex flex-col gap-0.5 ${idx < resolvedBreakdown.length - 1 ? "border-r border-gray-200 dark:border-neutral-700 pr-3 mr-3" : ""
                    }`}
                >
                  {/* Logo + label */}
                  <div className="flex items-center gap-1 min-w-0">
                    {item.icon && (
                      <img src={item.icon} alt={item.label} className="h-3.5 w-3.5 object-contain flex-shrink-0" />
                    )}
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider truncate">
                      {item.label}
                    </span>
                  </div>

                  {/* Animated value */}
                  <AnimatedValue
                    value={breakdownDisplayVal}
                    isCurrency={isCurrency}
                    isPercentage={isPercentage}
                    currency={currency}
                    className={`text-sm font-bold leading-none ${isZero ? "text-gray-300 dark:text-gray-600" : (item.valueClassName || "text-gray-700 dark:text-gray-200")}`}
                  />
                </div>
              );

              if (item.tooltipItems && item.tooltipItems.length > 0) {
                return (
                  <Tooltip
                    key={item.label}
                    placement="bottom"
                    content={
                      <div className="flex flex-col gap-2 min-w-48">
                        {item.tooltipItems.map((tItem) => (
                          <div key={tItem.label} className="flex flex-col gap-0.5">
                            <div className="flex items-center justify-between gap-4">
                              <span className={`text-xs font-semibold ${tItem.labelClassName || ""}`}>
                                {tItem.label}:
                              </span>
                              <span className={`text-xs font-bold ${tItem.valueClassName || "text-white"}`}>
                                {fmtStatic(tItem.value)}
                              </span>
                            </div>
                            {tItem.children && tItem.children.length > 0 && (
                              <div className="pl-2 border-l border-gray-500 flex flex-col gap-0.5">
                                {tItem.children.map((c) => (
                                  <div key={c.label} className="flex items-center justify-between gap-4">
                                    <span className="text-[10px] text-gray-300">{c.label}</span>
                                    <span className="text-[10px] text-gray-200 font-semibold">{fmtStatic(c.value)}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    }
                  >
                    {chip}
                  </Tooltip>
                );
              }

              return chip;
            })}
          </div>
        </div>
      )}

      {/* Legacy subStats */}
      {subStats && subStats.length > 0 && (
        <div className="border-t border-gray-100 dark:border-neutral-700 px-4 py-3 flex flex-wrap gap-4">
          {subStats.map((item) => (
            <div key={item.label} className="flex flex-col">
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{item.label}</span>
              <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">{fmtStatic(item.value as number)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default StatCard;
